import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter, HostListener, Inject,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from "@angular/core";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";
import {TableColumnSettings} from "../../../../commons/angular/services/table-column-manager";
import * as _ from "lodash";
import {PlanningViewSettingsResourcesModel} from "../../../models/planning-view-settings.model";
import {DEFAULT_RESOURCES_COLUMN_WIDTHS} from "../../../constants/planning-column-widths.constant";
import {RESOURCES_COLUMN_GROUPS} from "../../../constants/planning-column-groups.constant";
import {
  CdkDragDrop,
} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {PlanningResourceModel} from "../../../../resources/models/planning-resource.model";

require('./planning-draft-resources-table-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-resources-table-list',
  templateUrl: './planning-draft-resources-table-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftResourcesTableListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() draft?: any;
  @Input() panel: PlanningPanelModel;
  @Input() panels: PlanningPanelModel[];
  @Input() resources: any[];
  @Input() routes: any[];
  @Input() jobs: any[];
  @Input() settings: PlanningViewSettingsResourcesModel;
  @Input() selectedIds: string[] = [];
  @Input() selectedJobIds: string[];
  @Input() itemElementSize?: string;
  @Input() routesMap: Object = {};

  @Output() onEditResourceAllocation = new EventEmitter<{resource:any ,route: any, event: any}>();
  @Output() onSelect = new EventEmitter<string[]>();
  @Output() onCreateRoute = new EventEmitter<{id: string, jobIds: string[]}>();
  @Output() onDispatch = new EventEmitter<string[]>();
  @Output() onDrilldown = new EventEmitter<{ resource: any, isFloating?: boolean }>();
  @Output() onUpdateRouteResource = new EventEmitter<{route: any, resourceId: string}>();
  @Output() onSettingsChange = new EventEmitter<PlanningViewSettingsResourcesModel>();

  get uniqueId(): string {
    return 'resources-' + this.panel?.id;
  }
  defaultWidths: { [key: string]: number } = DEFAULT_RESOURCES_COLUMN_WIDTHS;
  columnGroups = RESOURCES_COLUMN_GROUPS;

  @ViewChild('headerContainer', {static: false}) private headerContainer: ElementRef;
  @ViewChild('bodyContainer', {read: ElementRef, static: false}) private bodyContainer: ElementRef;
  @ViewChild(CdkVirtualScrollViewport, {static: false}) private viewport: CdkVirtualScrollViewport;

  private resizeObserver: ResizeObserver;
  private scrollEndTimer: any | null = null;
  private clickTimeout: any | null = null;

  public connectedPanelIds: string[];
  public selectedKeys: Object;
  public weightUnit: string = this.splitService.getShortWeightUnit();
  public volumeUnit: string = this.splitService.getVolumeUnit();
  public severityThresholds: { low: number; mid: number; } = { low: 80, mid: 99 };
  public resourceTotalsMap: {
    [resourceId: string]: {
      totalLoad: number;
      totalVolume: number;
      totalPackagesCount: number;
      totalSpots: number;
      totalPallets: number;
      totalIncome: number;
      totalDuration: number;
    }
  } = {};

  constructor(
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject('planningDialogService') private planningDialogService,
    @Inject('planningCommonService') private planningCommonService,
    @Inject('splitService') private splitService,
    @Inject('$timeout') private $timeout,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService
    ) {
  }

  ngOnInit() {
    this.resizeObserver = new ResizeObserver(() => {
      this.viewport?.checkViewportSize();
      this.syncHeaderScrollbarWidth();
    });
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();

  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.viewport?.checkViewportSize();
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  onScroll(_: Event): void {
    if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
      }
      this.scrollEndTimer = null;
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resources || changes.routes) {
      const resourceIds = _.map(this.resources, 'id');
      const panelIds = _.map(this.panels, 'id');
      const routeIds = _.map(this.routes, 'id');
      this.connectedPanelIds = _.uniq(_.concat(panelIds || [], resourceIds || [], routeIds || []));
    }
    if (changes.selectedIds && !this.selectedIds) { this.selectedIds = []; }
    if (changes.selectedIds) {
      this.selectedKeys = {};
      _.forEach(this.selectedIds, (id) => {
        this.selectedKeys[id] = true;
      });
    }

    if (changes.resources && this.resources?.length) {
      this.viewport?.checkViewportSize();
    }

    if (changes.resources || changes.routesMap) {
      this.computeResourceTotals();
    }

    this.cdr.detectChanges();
  }

  computeResourceTotals() {
    this.resourceTotalsMap = {};
    if (!this.resources) {
      return;
    }
    _.forEach(this.resources, (resource) => {
      const routes = this.routesMap[resource.id] || [];
      let totalLoad = 0;
      let totalVolume = 0;
      let totalPackagesCount = 0;
      let totalSpots = 0;
      let totalPallets = 0;
      let totalIncome = 0;
      let totalDuration = 0;

      _.forEach(routes, (route) => {
        totalLoad += route.totals?.totalLoad || 0;
        totalVolume += route.totals?.totalVolume || 0;
        totalPackagesCount += route.totals?.totalPackagesCount || 0;
        totalSpots += route.totals?.totalSpots || 0;
        totalPallets += route.totals?.totalPallets || 0;
        totalIncome += route.totals?.totalIncome || 0;
        totalDuration += route.totals?.totalTimeSec || 0;
      });

      this.resourceTotalsMap[resource.id] = {
        totalLoad,
        totalVolume,
        totalPackagesCount,
        totalSpots,
        totalPallets,
        totalIncome,
        totalDuration
      };
    });
  }

  trackById(_: number, item: { id: string | number }) {
    return item.id;
  }

  onEditResourceAllocationClick(resource: any, event: any) {
    this.onEditResourceAllocation.emit({resource: resource, route: this.routesMap[resource.id]?.[0], event: event});
  }

  onRowClick(resource: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.clickTimeout = setTimeout(() => {
      this.onDrilldown.emit({ resource, isFloating: false });
      this.clickTimeout = null;
    }, 250);
  }

  onRowDoubleClick(resource: any) {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }
    this.onDrilldown.emit({ resource, isFloating: true });
  }

  onToggleSelectedResource(id, isSelected: boolean) {
    this.selectedKeys[id] = isSelected;
    const selectedIds = _.map(this.selectedKeys, (value, key) => {
      if (value) return key;
    });

    this.onSelect.emit(_.compact(selectedIds));
  }

  onDrop(resource: any, event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropResources(
      resource,
      event,
      this.selectedJobIds,
      this.jobs
    ).then((result) => {
      if (result) {
        if (result.type === 'update') {
          this.onUpdateRouteResource.emit(result.data);
        } else if (result.type === 'create') {
          this.onCreateRoute.emit(result.data);
        }
      }
    });
  }

  getResourceColor(resource: any) {
    const route = this.routesMap[resource.id]?.[0];
    return route ? this.planningCommonService.getRouteColor(route) : undefined;
  }

  getResourceTotalDuration(resource: any) {
    return this.resourceTotalsMap[resource.id]?.totalDuration || 0;
  }

  onDispatchClick(resource: PlanningResourceModel) {
    const routeIds: string[] = this.routes.reduce((acc, route) => {
      if (route.resourceId === resource.id) {
        acc.push(route.id);
      }
      return acc;
    }, []);

    this.onDispatch.emit(routeIds);
  }

  getWeightSeverity(resource: any): string {
    const totals = this.resourceTotalsMap[resource.id];
    return this.getSeverity(totals?.totalLoad || 0, resource?.capacity?.weight);
  }

  getVolumeSeverity(resource: any): string {
    const totals = this.resourceTotalsMap[resource.id];
    const volumeCapacity = this.getVolumeCapacity(resource);
    return this.getSeverity(totals?.totalVolume || 0, volumeCapacity);
  }

  getPackagesSeverity(resource: any): string {
    const totals = this.resourceTotalsMap[resource.id];
    return this.getSeverity(totals?.totalPackagesCount || 0, resource?.capacity?.numberOfPackages);
  }

  getSpotsSeverity(resource: any): string {
    const totals = this.resourceTotalsMap[resource.id];
    return this.getSeverity(totals?.totalSpots || 0, resource?.capacity?.numberOfSpots);
  }

  getPalletsSeverity(resource: any): string {
    const totals = this.resourceTotalsMap[resource.id];
    return this.getSeverity(totals?.totalPallets || 0, resource?.capacity?.numberOfPallets);
  }

  getVolumeCapacity(resource: any) {
    return resource.capacity?.dimensions ?
      (resource.capacity.dimensions.height * resource.capacity.dimensions.width * resource.capacity.dimensions.length) : 0;
  }

  getSeverity(value: number, capacity?: number): string {
    if (!capacity) {
      return '';
    }
    const ratio = (value / capacity) * 100;
    return (ratio < this.severityThresholds.low) ? 'low' : ((ratio >= this.severityThresholds.low && ratio < this.severityThresholds.mid) ? 'mid' : 'high');
  }
}
